import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Typography,
  OutlinedInput,
  ListItemText,
  Checkbox,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import EditModal from '../components/common/EditModal';
import DeleteButton from '../components/common/DeleteButton';
import Tooltip from '../components/common/Tooltip';

interface Column {
  id: keyof Data | 'action';
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
}

interface Data {
  name: string;
  code: string;
  types: string;
  'Country Code': string;
  logoURL: string;
  websiteURL: string;
  'CheckIn link': string;
  status: string;
  createdBy: string;
}

const columns: readonly Column[] = [
  { id: 'name', label: 'Name', minWidth: 150 },
  { id: 'code', label: 'Code', minWidth: 80 },
  { id: 'types', label: 'Types', minWidth: 120 },
  { id: 'Country Code', label: 'Country Code', minWidth: 120 },
  { id: 'logoURL', label: 'Logo URL', minWidth: 180 },
  { id: 'websiteURL', label: 'Website URL', minWidth: 180 },
  { id: 'CheckIn link', label: 'Check-In Link', minWidth: 180 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'createdBy', label: 'Created By', minWidth: 120 },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center' },
];

function createData(
  name: string,
  code: string,
  types: string,
  countryCode: string,
  logoURL: string,
  websiteURL: string,
  checkInLink: string,
  status: string,
  createdBy: string
): Data {
  return {
    code,
    name,
    types,
    'Country Code': countryCode,
    logoURL,
    websiteURL,
    'CheckIn link': checkInLink,
    status,
    createdBy,
  };
}

const initialRows: Data[] = [
  createData(
    'Air India',
    'AI',
    'International, Domestic',
    'IN',
    'https://logo.ai.com/logo.png',
    'https://www.airindia.com',
    'https://checkin.airindia.com',
    'Active',
    'Admin'
  ),
  createData(
    'British Airways',
    'BA',
    'International',
    'UK',
    'https://logo.ba.com/logo.png',
    'https://www.britishairways.com',
    'https://checkin.britishairways.com',
    'Active',
    'Emma'
  ),
  createData(
    'United Airlines',
    'UA',
    'International',
    'US',
    'https://logo.ua.com/logo.png',
    'https://www.united.com',
    'https://checkin.united.com',
    'Inactive',
    'John'
  ),
];

const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProp = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
    anchorOrigin: {
      vertical: 'bottom' as 'bottom',
      horizontal: 'left' as 'left',
    },
    transformOrigin: {
      vertical: 'top' as 'top',
      horizontal: 'left' as 'left',
    },
    getContentAnchorEl: null, // Important for older versions of MUI v4; can omit for MUI v5+
  };

const label = [
  'Name',
  'Code',
  'Types',
  'Country Code',
  'Logo URL',
  'Website URL',
  'CheckIn Link'
];

const Airlines = () => {
  const [rows, setRows] = useState<Data[]>(initialRows);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEditSave = (updatedData: Data) => {
    if (selectedRowIndex === null) return;
    const updatedRows = [...rows];
    updatedRows[selectedRowIndex] = updatedData;
    setRows(updatedRows);
    setEditOpen(false);
  };

  const renderStatusButton = (status: string) => (
    <Button
      variant="contained"
      size="small"
      sx={{
        backgroundColor: status === 'Active' ? '#1cad2d' : '#f3030f',
        color: 'white',
        textTransform: 'none',
        fontWeight: 'bold',
        '&:hover': {
          backgroundColor: status === 'Active' ? '#2e7d32' : '#c62828',
        },
      }}
    >
      {status}
    </Button>
  );

    const handleDeleteRow = (code: string) => {
      const updatedRows = rows.filter((row) => row.code !== code);
      setRows(updatedRows);
      console.log(`Deleted row with code: ${code}`);
    };

  const renderActions = (row: Data, index: number) => (
    <>
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          setSelectedRowIndex(index);
          setEditOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>
      <IconButton color="error" size="small">
        <DeleteButton
        onDelete={() => handleDeleteRow(row.code)}
        confirmMessage="Do you really want to delete this item?"
      />
        </IconButton>
    </>
  );

  const [labelName, setLabelName] = React.useState<string[]>([]);
          
            const labelChange = (event: SelectChangeEvent<typeof labelName>) => {
              const {
                target: { value },
              } = event;
              setLabelName(
                // On autofill we get a stringified value.
                typeof value === 'string' ? value.split(',') : value,
              );
            };

  return (
    <>
      <Box className="headingBtnWrap">
        <Typography variant="h1">Airlines</Typography>
        <CreateButtonWithModal modalTitle="Airlines">
          <Box className="modalFormWrap">
            <Box component="form" className="modalForm">
              {['Name', 'Code', 'Types', 'Country Code', 'Logo URL', 'Website URL', 'Check-In Link'].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" className="formBtn">
                Save
              </Button>
            </Box>
          </Box>
          <BlackWhiteSwitch />
        </CreateButtonWithModal>
      </Box>

      <Box className="searchInputWrap">
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          p={2}
                          sx={{ width: '100%'}}
                          className="searchInputFilterBox"
                        >
                          <FormControl sx={{ minWidth: 460 }}>
                             <Select
                              className='leftFilterBox'
                              labelId="demo-multiple-checkbox-label"
                              id="demo-multiple-checkbox"
                              multiple
                              value={labelName}
                              onChange={labelChange}
                              displayEmpty
                              input={<OutlinedInput />}
                              renderValue={(selected) => {
                                if (selected.length === 0) {
                                  return 'Select';  // <-- Placeholder text
                                }
                                return selected.join(', ');
                              }}
                              MenuProps={MenuProp}
                            >
                              <MenuItem disabled value="">
                              Select
                              </MenuItem>
                              {label.map((name) => (
                                <MenuItem key={name} value={name} className='selectDropdown'>
                                  <Checkbox checked={labelName.includes(name)} />
                                  <ListItemText primary={name} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                                  variant="contained"
                                  className="searchBtn"
                                  color="primary"
                                  type="submit"
                                  sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                >
                                  Search
                                </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }} className="dynamicInputFieldWrap">
                          {labelName.map((item) => (
                            <div key={item} className="form-control inputFieldWrap">
                              <label style={{ marginBottom: '4px', fontWeight: '500' }}>{item}</label>
                              <input
                                type="text"
                                // placeholder={`Enter ${item}`}
                                className="inputField"
                              />
                            </div>
                          ))}
                        </Box>
                      </Box>

      {/* Table */}
      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: 100 }} align="center">Audit Trail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => (
                <TableRow hover key={rowIndex}>
                  {columns.map((column) => {
                    const value = row[column.id as keyof Data];
                    if (column.id === 'status') {
                      return <TableCell key={column.id}>{renderStatusButton(value)}</TableCell>;
                    }
                    if (column.id === 'action') {
                      return (
                        <TableCell key={column.id} align="center">
                          {renderActions(row, rowIndex)}
                        </TableCell>
                      );
                    }
                    
                    const isLink = column.id.includes('URL') || column.id.includes('link');
                    return (
                      <TableCell key={column.id}>
                        {isLink ? (
                          <a href={value} target="_blank" rel="noopener noreferrer">
                            {value}
                          </a>
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <Tooltip />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Edit Modal */}
      {selectedRowIndex !== null && (
        <EditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          initialData={rows[selectedRowIndex]}
          onSave={handleEditSave}
          excludeFields={['action', 'status']}
          readOnlyFields={['createdBy']}
          title="Edit Airlines"
        />
      )}
    </>
  );
};

export default Airlines;
